import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { PLATFORM, platformMeta, appStatusMeta } from "@/utils/deviceInsightsUtils";
import { DEVICE_COLORS } from "@/config/colors";
import { DataGrid } from "@mui/x-data-grid";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";

const ORANGE = "#ea580c";
const ORANGE_LIGHT = "#fff7ed";

function fmtDate(iso) {
  if (!iso) return "—";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0].slice(2)}` : iso;
}

function fmtDateTime(str) {
  if (!str) return "—";
  try {
    const [datePart = "", timePart = ""] = String(str).replace("T", " ").split(" ");
    const [, m, d] = datePart.split("-");
    const [hh = "0", mm = "00"] = timePart.split(":");
    const h = Number(hh);
    const ampm = h >= 12 ? "pm" : "am";
    const h12 = h % 12 || 12;
    return `${d}/${m} ${h12}:${mm}${ampm}`;
  } catch {
    return String(str);
  }
}

const STATUS_COLORS = {
  "None":                      "#94a3b8",
  "Requested":                 "#d97706",
  "Prepared":                  "#9333ea",
  "Routed":                    "#7c3aed",
  "Loaded":                    "#0891b2",
  "Out for Delivery":          "#0284c7",
  "Next Delivery":             "#0369a1",
  "Delivered":                 "#16a34a",
  "Partially Delivered":       "#65a30d",
  "Pharmacy Cancelled":        "#dc2626",
  "Not Delivered":             "#dc2626",
  "Rescheduled":               "#f59e0b",
  "Draft":                     "#94a3b8",
  "Sent to Courier":           "#e87c57",
  "Assigned to Courier":       "#d4613c",
  "Awaiting Pickup (Courier)": "#f59e0b",
  "Arriving at Delivery":      "#0284c7",
  "Courier Refunded":          "#6b7280",
  "Courier on Hold":           "#9333ea",
};

function StatusChip({ label, size = "small" }) {
  const color = STATUS_COLORS[label] ?? "#64748b";
  return (
    <Chip
      label={label}
      size={size}
      sx={{ backgroundColor: `${color}20`, color, fontWeight: 700, fontSize: 11 }}
    />
  );
}

function StarRating({ value = 0 }) {
  const v = Math.round(Number(value) || 0);
  return (
    <Box sx={{ display: "inline-flex", gap: "1px", alignItems: "center" }} aria-label={`${v} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} style={{ color: i <= v ? "#f59e0b" : "#d1d5db", fontSize: 14, lineHeight: 1 }}>★</span>
      ))}
    </Box>
  );
}

// ── Delivery Status Log Drawer ────────────────────────────────────────────────

function DeliveryLogDrawer({ open, onClose, delivery, chemistUUID, isManager }) {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !delivery) return;
    setLog(null);
    setError(null);
    setLoading(true);
    const path = isManager
      ? API_PATHS.MANAGER_CHEMIST_MEDIDROP_DELIVERY_LOG
      : API_PATHS.CHEMIST_MEDIDROP_DELIVERY_LOG;
    phappsRequest(path, "GET", null, { ChemistUUID: chemistUUID, DeliveryID: delivery.DeliveryID })
      .then((data) => setLog(data))
      .catch(() => setError("Failed to load delivery status history."))
      .finally(() => setLoading(false));
  }, [open, delivery, chemistUUID, isManager]);

  const recipientName = delivery
    ? delivery.DeliverToName ||
      [delivery.FirstName, delivery.LastName].filter(Boolean).join(" ") ||
      "Unknown recipient"
    : "";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 0 } }}
    >
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 2, borderBottom: "1px solid #e5e7eb",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        backgroundColor: ORANGE,
      }}>
        <Box>
          <Typography fontWeight={700} fontSize="0.95rem" color="#fff">
            Delivery #{delivery?.ChemDeliveryID ?? delivery?.DeliveryID}
          </Typography>
          <Typography fontSize="0.78rem" sx={{ color: "rgba(255,255,255,0.75)", mt: 0.25 }}>
            {recipientName} · {fmtDate(delivery?.SchedDeliveryDate)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Current status + address */}
      {delivery && (
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", flexDirection: "column", gap: 0.75 }}>
          <StatusChip label={delivery.StatusLabel} />
          {delivery.VerifiedFullAddress && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {delivery.VerifiedFullAddress}
            </Typography>
          )}
          {delivery.Packages > 1 && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {delivery.Packages} packages
            </Typography>
          )}
        </Box>
      )}

      {/* Timeline */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
        <Typography fontWeight={700} fontSize="0.78rem" sx={{ textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", mb: 1.5 }}>
          Status History
        </Typography>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} sx={{ color: ORANGE }} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {log && log.length === 0 && (
          <Typography variant="body2" color="text.secondary">No status changes recorded.</Typography>
        )}

        {log && log.length > 0 && (
          <Box sx={{ position: "relative" }}>
            {/* Vertical line */}
            <Box sx={{ position: "absolute", left: 9, top: 8, bottom: 8, width: 2, backgroundColor: "#e5e7eb" }} />

            {log.map((entry, i) => {
              const color = STATUS_COLORS[entry.StatusLabel] ?? "#64748b";
              const isLast = i === log.length - 1;
              return (
                <Box key={entry.StatusLogID} sx={{ display: "flex", gap: 2, mb: isLast ? 0 : 2, position: "relative" }}>
                  {/* Dot */}
                  <Box sx={{ flexShrink: 0, width: 20, display: "flex", justifyContent: "center", pt: 0.25 }}>
                    <Box sx={{
                      width: 10, height: 10, borderRadius: "50%",
                      backgroundColor: color,
                      border: "2px solid #fff",
                      boxShadow: `0 0 0 2px ${color}44`,
                      position: "relative", zIndex: 1,
                    }} />
                  </Box>
                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      label={entry.StatusLabel}
                      size="small"
                      sx={{ backgroundColor: `${color}18`, color, fontWeight: 700, fontSize: 10, height: 20 }}
                    />
                    <Typography fontSize="0.72rem" color="text.secondary" sx={{ mt: 0.25 }}>
                      {fmtDateTime(entry.StatusChangeDate)}
                    </Typography>
                    {entry.Reason && (
                      <Typography fontSize="0.78rem" sx={{ mt: 0.5, color: "#374151", fontStyle: "italic" }}>
                        "{entry.Reason}"
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

// Inline device line for a driver row (spec Option A).
function DriverDeviceLine({ platform, model, osVersion, osVersionKnown, appVersion, appStatus }) {
  if (!platform && !model) {
    return (
      <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>
        No active device
      </Typography>
    );
  }
  const m = platformMeta(platform);
  const Icon = platform === PLATFORM.ANDROID ? AndroidIcon : platform === PLATFORM.IOS ? AppleIcon : DevicesOtherIcon;
  const sm = appStatus ? appStatusMeta(appStatus) : null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
      <Icon sx={{ fontSize: 14, color: m.color }} />
      <Typography variant="caption" sx={{ color: "text.secondary" }} noWrap>
        {model || m.label}
        {osVersion ? " · " : ""}
        <Typography component="span" variant="caption" sx={{ color: osVersionKnown ? "text.secondary" : "text.disabled", fontWeight: 600 }}>
          {osVersion || ""}
        </Typography>
        {appVersion ? (
          <Typography component="span" variant="caption" sx={{ color: "text.disabled" }}> · {appVersion}</Typography>
        ) : null}
      </Typography>
      {sm && (
        <Chip label={sm.label} size="small" sx={{ height: 16, fontSize: 9.5, fontWeight: 700, backgroundColor: sm.tint, color: sm.color, "& .MuiChip-label": { px: 0.75 } }} />
      )}
    </Box>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#f9fafb", display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: ORANGE, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: "text.secondary" }}>{sub}</Typography>}
    </Box>
  );
}

function buildDeliveryColumns() {
  return [
    {
      field: "SchedDeliveryDate",
      headerName: "Date",
      width: 80,
      renderCell: (p) => <Typography variant="body2">{fmtDate(p.value)}</Typography>,
    },
    {
      field: "recipient",
      headerName: "Recipient",
      flex: 1,
      minWidth: 120,
      renderCell: (p) => (
        <Typography variant="body2" noWrap sx={{ width: 1 }}>
          {p.row.DeliverToName ||
            [p.row.FirstName, p.row.LastName].filter(Boolean).join(" ") ||
            "—"}
        </Typography>
      ),
    },
    {
      field: "StatusLabel",
      headerName: "Status",
      width: 140,
      renderCell: (p) => <StatusChip label={p.value || "—"} />,
    },
  ];
}

// ── Main component ────────────────────────────────────────────────────────────

const ChemistMediDropTab = ({ chemistUUID, isManager = false }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  const handleOpenDelivery = (row) => {
    setSelectedDelivery(row);
    setDrawerOpen(true);
  };

  const fetchData = useCallback(async (force = false) => {
    if (!chemistUUID || (data && !force)) return;
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(
        isManager ? API_PATHS.MANAGER_CHEMIST_MEDIDROP : API_PATHS.CHEMIST_MEDIDROP,
        "GET", null, { ChemistUUID: chemistUUID }
      );
      setData(res);
    } catch (e) {
      setError(getAdminApiErrorMessage(e, "Failed to load MediDrop data"));
    } finally {
      setLoading(false);
    }
  }, [chemistUUID, isManager]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
      <Typography color="textSecondary">Loading MediDrop data…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ py: 4, px: 2 }}>
      <Alert severity="warning">{error}</Alert>
    </Box>
  );

  if (!data) return null;

  const { Metrics, DeliveriesPerDay, StatusBreakdown, Drivers, RecentDeliveries, Routes, DeviceSummary, RecentReviews } = data;

  const deliveryRows = (RecentDeliveries ?? []).map((d, i) => ({ id: d.DeliveryID ?? i, ...d }));
  const deliveryColumns = buildDeliveryColumns();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <DeliveryLogDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        delivery={selectedDelivery}
        chemistUUID={chemistUUID}
        isManager={isManager}
      />

      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 1.5 }}>
        <StatCard
          label="Deliveries (30d)"
          value={Number(Metrics?.TotalDeliveries30d ?? 0).toLocaleString()}
          sub={`~${Number(Metrics?.AvgDeliveriesPerDay ?? 0).toFixed(1)}/day`}
        />
        <StatCard
          label="Existing Drafts"
          value={Number(Metrics?.DraftCount ?? 0).toLocaleString()}
        />
        <StatCard
          label="Routes (30d)"
          value={Number(Metrics?.RoutesCount30d ?? 0).toLocaleString()}
        />
        <StatCard
          label="Drivers"
          value={Number(Metrics?.DriversCount ?? 0).toLocaleString()}
          sub="with driver ID"
        />
        <StatCard
          label="All-time deliveries"
          value={Number(Metrics?.TotalDeliveriesAllTime ?? 0).toLocaleString()}
        />
      </Box>

      {/* Top row: Chart (left 50%) | Recent deliveries (right 50%) — both cards 340px */}
      {/* Chart card: 16(pt) + 28(title) + 280(chart) + 16(pb) = 340px                  */}
      {/* DataGrid:   340 - 16(pt) - 46(title flex+mb) - 16(pb) = 262px                 */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>

        {/* Chart card */}
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORANGE, mb: 1 }}>
            Deliveries per day (30d)
          </Typography>
          <Box sx={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={DeliveriesPerDay} margin={{ top: 4, right: 8, left: -20, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 9 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => { const p = String(v).split("-"); return p.length === 3 ? `${p[2]}/${p[1]}` : v; }}
                  interval="preserveStartEnd"
                />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <ReTooltip
                  formatter={(v) => [v, "Deliveries"]}
                  labelFormatter={(v) => { const p = String(v).split("-"); return `Date: ${p.length === 3 ? `${p[2]}/${p[1]}` : v}`; }}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                />
                <Bar dataKey="count" fill={ORANGE} radius={[3, 3, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* Recent deliveries — fixed height 340px matching chart card */}
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORANGE }}>
              Recent deliveries (last 30 days)
            </Typography>
            <Tooltip title="Refresh delivery list">
              <IconButton size="small" onClick={() => fetchData(true)} disabled={loading} sx={{ color: ORANGE }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {deliveryRows.length > 0 ? (
            <Box sx={{ height: 262 }}>
              <DataGrid
                rows={deliveryRows}
                columns={deliveryColumns}
                density="compact"
                disableColumnMenu
                disableRowSelectionOnClick
                hideFooter={deliveryRows.length <= 25}
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                onRowClick={(params) => handleOpenDelivery(params.row)}
                sx={{ ...pharmacyHaloDataGridSx, height: "100%", "& .MuiDataGrid-row": { cursor: "pointer" } }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No deliveries in the last 30 days.</Typography>
          )}
        </Box>
      </Box>

      {/* Bottom row: Status breakdown | Routes | Drivers | Recent reviews */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "repeat(4, 1fr)" }, gap: 2 }}>
        {/* Status breakdown */}
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORANGE, mb: 1.5 }}>
            Status breakdown (30d)
          </Typography>
          {StatusBreakdown && StatusBreakdown.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {(() => {
                const nonDraft = StatusBreakdown.filter((s) => s.status !== 12);
                const total = nonDraft.reduce((acc, r) => acc + r.count, 0);
                return StatusBreakdown.map((s) => {
                  const color = STATUS_COLORS[s.label] ?? "#64748b";
                  const isDraft = s.status === 12;
                  const pct = !isDraft && total ? Math.round((s.count / total) * 100) : null;
                  return (
                    <Box key={s.status} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>{s.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color, minWidth: 28, textAlign: "right" }}>{s.count}</Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", minWidth: 36, textAlign: "right" }}>
                        {pct !== null ? `${pct}%` : "—"}
                      </Typography>
                    </Box>
                  );
                });
              })()}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No deliveries in the last 30 days.</Typography>
          )}
        </Box>

        {/* Routes */}
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORANGE, mb: 1.5 }}>
            Routes (30d · {Routes?.length ?? 0})
          </Typography>
          {Routes && Routes.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, maxHeight: 346, overflowY: "auto" }}>
              {Routes.map((r) => (
                <Box key={r.RouteID} sx={{ display: "flex", alignItems: "center", gap: 1, p: 0.75, borderRadius: 1, backgroundColor: ORANGE_LIGHT }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.78rem" }}>
                      Route #{r.ChemRouteID ?? r.RouteID}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {fmtDate(r.RouteDate)} · {r.StopCount} stop{r.StopCount !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  {r.RouteUrl ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => window.open(r.RouteUrl, "_blank", "noopener,noreferrer")}
                      sx={{ fontSize: "0.68rem", py: 0.2, px: 1, minWidth: 0, borderColor: ORANGE, color: ORANGE, fontWeight: 600, flexShrink: 0 }}
                    >
                      Open
                    </Button>
                  ) : (
                    <Typography variant="caption" sx={{ color: "text.disabled", flexShrink: 0 }}>No link</Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No routes in the last 30 days.</Typography>
          )}
        </Box>

        {/* Drivers */}
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, gap: 1, flexWrap: "wrap" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORANGE }}>
              Drivers ({Drivers?.length ?? 0})
              <Typography component="span" variant="caption" sx={{ color: "text.secondary", fontWeight: 600, ml: 0.75 }}>
                · Routes (30d)
              </Typography>
            </Typography>
            {DeviceSummary && (DeviceSummary.android > 0 || DeviceSummary.ios > 0) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                  <AndroidIcon sx={{ fontSize: 14, color: DEVICE_COLORS.ANDROID_DARK }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>{DeviceSummary.android}</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "text.disabled" }}>·</Typography>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                  <AppleIcon sx={{ fontSize: 14, color: DEVICE_COLORS.IOS_DARK }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>{DeviceSummary.ios}</Typography>
                </Box>
                {DeviceSummary.oldApp > 0 && (
                  <Tooltip title="Drivers running an app version below the latest target">
                    <Chip label={`${DeviceSummary.oldApp} on old app`} size="small"
                      sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: DEVICE_COLORS.OUTDATED_TINT, color: DEVICE_COLORS.OUTDATED }} />
                  </Tooltip>
                )}
                {DeviceSummary.outdatedOs > 0 && (
                  <Tooltip title="Drivers on an outdated operating system">
                    <Chip label={`${DeviceSummary.outdatedOs} outdated OS`} size="small"
                      sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: "#fdecea", color: "#c0392b" }} />
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
          {Drivers && Drivers.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 346, overflowY: "auto", pr: 0.5 }}>
              {Drivers.map((d) => (
                <Box key={d.StaffID} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1, borderRadius: 1, backgroundColor: ORANGE_LIGHT }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{d.Name || "—"}</Typography>
                    {d.Phone && <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>{d.Phone}</Typography>}
                    {d.SsoEmail && (
                      <Tooltip title="Connected SSO account">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                          <MailOutlineIcon sx={{ fontSize: 13, color: "text.disabled", flexShrink: 0 }} />
                          <Typography variant="caption" noWrap sx={{ color: "text.secondary" }}>{d.SsoEmail}</Typography>
                        </Box>
                      </Tooltip>
                    )}
                    <DriverDeviceLine platform={d.Platform} model={d.DeviceModel} osVersion={d.OsVersion} osVersionKnown={d.OsVersionKnown} appVersion={d.AppVersion} appStatus={d.AppStatus} />
                  </Box>
                  <Tooltip title="Routes completed in the last 30 days">
                    <Typography variant="body1" sx={{ fontWeight: 800, color: ORANGE, lineHeight: 1, flexShrink: 0 }}>
                      {d.RoutesDone30d}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No drivers with a Driver ID assigned.</Typography>
          )}
        </Box>

        {/* Recent reviews */}
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #e5e7eb", backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORANGE, mb: 1.5 }}>
            Recent reviews ({RecentReviews?.length ?? 0})
          </Typography>
          {RecentReviews && RecentReviews.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 346, overflowY: "auto", pr: 0.5 }}>
              {RecentReviews.map((rv) => (
                <Box key={rv.ReviewID} sx={{ p: 1, borderRadius: 1, backgroundColor: ORANGE_LIGHT }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <StarRating value={rv.Stars} />
                    <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>{fmtDate(rv.ReviewDate)}</Typography>
                  </Box>
                  {rv.Comment ? (
                    <Typography variant="body2" sx={{ mt: 0.5, color: "#374151", fontSize: "0.8rem", lineHeight: 1.4 }}>
                      “{rv.Comment}”
                    </Typography>
                  ) : (
                    <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>No comment left</Typography>
                  )}
                  {rv.DriverName && (
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
                      Driver: {rv.DriverName}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No reviews yet.</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChemistMediDropTab;
